// src/pages/ApprovalPending.jsx
import React, {
  useState, useEffect, useRef, useMemo, useCallback,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';

/* ================================================================== */
/* Constants                                                           */
/* ================================================================== */

const SUPPORT_EMAIL = 'info@iiee-ercsa.org';
const SLA_HOURS = 48;
const REDIRECT_DELAY_MS = 1400;
const COPY_FEEDBACK_MS = 2000;

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

/** Safe timestamp → Date. */
const safeToDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.toMillis === 'function') return new Date(value.toMillis());
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const hoursSince = (date) => {
  if (!date) return null;
  return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60));
};

const formatRemaining = (hoursLeft) => {
  if (hoursLeft <= 0) return 'Any time now';
  if (hoursLeft >= 24) {
    const days = Math.ceil(hoursLeft / 24);
    return `Usually within ${days} day${days > 1 ? 's' : ''}`;
  }
  return `Usually within ${hoursLeft} hour${hoursLeft > 1 ? 's' : ''}`;
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

/* ================================================================== */
/* Skeleton                                                            */
/* ================================================================== */

const StatusSkeleton = () => (
  <div
    role="status"
    aria-live="polite"
    className="w-full max-w-md"
  >
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center animate-pulse">
      <div className="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-full mx-auto mb-6" />
      <div className="h-6 w-56 bg-gray-200 dark:bg-gray-700 rounded mx-auto mb-3" />
      <div className="h-4 w-72 bg-gray-200 dark:bg-gray-700 rounded mx-auto mb-3" />
      <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg mb-6" />
      <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg mb-3" />
      <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
    </div>
  </div>
);

/* ================================================================== */
/* Progress timeline                                                   */
/* ================================================================== */

const ProgressBar = ({ created }) => {
  const hoursPassed = hoursSince(created) ?? 0;
  const percent = Math.min(
    100,
    Math.max(4, Math.round((hoursPassed / SLA_HOURS) * 100)),
  );

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-gray-500">Progress</span>
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          {percent}%
        </span>
      </div>
      <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className={`h-full rounded-full ${
            percent >= 90
              ? 'bg-orange'
              : percent >= 50
              ? 'bg-yellow-500'
              : 'bg-yellow-400'
          }`}
        />
      </div>
    </div>
  );
};

/* ================================================================== */
/* Copy-to-clipboard button                                            */
/* ================================================================== */

const CopyEmailButton = ({ email }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
      } else {
        const ta = document.createElement('textarea');
        ta.value = email;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    } catch (err) {
      console.warn('Copy failed:', err);
    }
  }, [email]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-xs text-orange hover:underline"
      aria-label={`Copy ${email}`}
    >
      <i
        className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}
        aria-hidden="true"
      />
      {copied ? 'Copied!' : 'Copy email'}
    </button>
  );
};

/* ================================================================== */
/* Approved confetti                                                   */
/* ================================================================== */

const ConfettiBurst = () => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 18 }).map((_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 220,
        y: -Math.random() * 180 - 40,
        rotate: Math.random() * 360,
        delay: Math.random() * 0.3,
        color: ['#f97316', '#22c55e', '#3b82f6', '#eab308', '#ec4899'][
          i % 5
        ],
      })),
    [],
  );

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-visible"
    >
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          initial={{ opacity: 0, x: 0, y: 0, scale: 0.5, rotate: 0 }}
          animate={{
            opacity: [0, 1, 1, 0],
            x: p.x,
            y: p.y,
            scale: 1,
            rotate: p.rotate,
          }}
          transition={{
            duration: 1.4,
            delay: p.delay,
            ease: 'easeOut',
          }}
          className="absolute left-1/2 top-1/2 block w-2 h-2 rounded-sm"
          style={{ backgroundColor: p.color }}
        />
      ))}
    </div>
  );
};

/* ================================================================== */
/* Subviews                                                            */
/* ================================================================== */

const PendingView = ({
  user,
  userData,
  timeRemaining,
  onSignOut,
  onRefresh,
  refreshing,
}) => (
  <>
    {/* Clock Icon */}
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.2, type: 'spring' }}
      className="w-24 h-24 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-6"
    >
      <i
        className="fas fa-clock text-yellow-600 dark:text-yellow-400 text-5xl"
        aria-hidden="true"
      />
    </motion.div>

    <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
      Account Pending Approval
    </h1>

    <p className="text-gray-600 dark:text-gray-300 mb-4">
      Hello{' '}
      <span className="font-semibold text-orange">
        {user?.displayName || user?.email}
      </span>
      ,
    </p>

    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 mb-6 text-left">
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
        <i
          className="fas fa-info-circle text-yellow-600 mr-2"
          aria-hidden="true"
        />
        <strong>Your account is pending admin approval.</strong>
      </p>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
        All new registrations are reviewed by an administrator before you can
        access the full features of the IIEE-ERCSA platform.
      </p>
    </div>

    {/* Timeline */}
    <div className="mb-4">
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="text-gray-500">Registration Date:</span>
        <span className="text-gray-700 dark:text-gray-300">
          {safeToDate(userData?.createdAt)?.toLocaleDateString() || 'Today'}
        </span>
      </div>
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="text-gray-500">Approval Status:</span>
        <span
          role="status"
          className="text-yellow-600 font-semibold flex items-center gap-1"
        >
          <i className="fas fa-spinner fa-pulse" aria-hidden="true" /> Pending
          Review
        </span>
      </div>
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="text-gray-500">Estimated Wait:</span>
        <span className="text-gray-700 dark:text-gray-300">
          {timeRemaining}
        </span>
      </div>
    </div>

    {/* Progress bar */}
    <ProgressBar created={safeToDate(userData?.createdAt)} />

    {/* What you can do while waiting */}
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-6">
      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
        <i className="fas fa-edit mr-2 text-orange" aria-hidden="true" />
        What you can do right now:
      </p>
      <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 text-left">
        <li className="flex items-center gap-2">
          <i
            className="fas fa-check-circle text-green-500 text-xs"
            aria-hidden="true"
          />
          Complete your profile information
        </li>
        <li className="flex items-center gap-2">
          <i
            className="fas fa-check-circle text-green-500 text-xs"
            aria-hidden="true"
          />
          Add your professional details
        </li>
        <li className="flex items-center gap-2">
          <i
            className="fas fa-check-circle text-green-500 text-xs"
            aria-hidden="true"
          />
          Upload your profile photo
        </li>
      </ul>
    </div>

    {/* Actions */}
    <div className="space-y-3">
      <Link
        to="/profile"
        className="w-full bg-orange hover:bg-orange-dark text-white font-semibold py-2.5 rounded-lg transition transform hover:scale-[1.02] flex items-center justify-center gap-2"
      >
        <i className="fas fa-user-edit" aria-hidden="true" />
        Complete Your Profile
      </Link>

      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium py-2.5 rounded-lg transition flex items-center justify-center gap-2 disabled:opacity-60"
      >
        <i
          className={`fas ${refreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}
          aria-hidden="true"
        />
        {refreshing ? 'Checking…' : 'Check Status Now'}
      </button>

      <button
        type="button"
        onClick={onSignOut}
        className="w-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2.5 rounded-lg transition flex items-center justify-center gap-2"
      >
        <i className="fas fa-sign-out-alt" aria-hidden="true" />
        Sign Out
      </button>
    </div>

    <div className="mt-6 flex flex-col items-center gap-1">
      <p className="text-xs text-gray-500 dark:text-gray-500">
        Need help?{' '}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="text-orange hover:underline"
        >
          Contact Administrator
        </a>
      </p>
      <CopyEmailButton email={SUPPORT_EMAIL} />
    </div>
  </>
);

const RejectedView = ({ userData, canResubmit = true }) => (
  <>
    <div className="w-24 h-24 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
      <i
        className="fas fa-times-circle text-red-500 text-5xl"
        aria-hidden="true"
      />
    </div>
    <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
      Registration Not Approved
    </h1>
    <p className="text-gray-600 dark:text-gray-300 mb-4">
      Unfortunately, your registration was not approved.
    </p>

    {userData?.rejectionReason && (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-6 text-left">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <strong>Reason:</strong> {userData.rejectionReason}
        </p>
      </div>
    )}

    {userData?.rejectedAt && (
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Reviewed on {formatDateTime(safeToDate(userData.rejectedAt))}
      </p>
    )}

    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
      If you believe this is a mistake, please contact the administrator.
    </p>

    <div className="space-y-3">
      <a
        href={`mailto:${SUPPORT_EMAIL}?subject=Appeal%20my%20registration%20decision`}
        className="inline-flex items-center justify-center gap-2 w-full bg-orange hover:bg-orange-dark text-white font-semibold py-2.5 rounded-lg transition"
      >
        <i className="fas fa-envelope" aria-hidden="true" />
        Appeal Decision
      </a>

      {canResubmit && (
        <Link
          to="/register"
          className="inline-flex items-center justify-center gap-2 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium py-2.5 rounded-lg transition"
        >
          <i className="fas fa-redo" aria-hidden="true" />
          Submit New Registration
        </Link>
      )}
    </div>

    <div className="mt-6 flex flex-col items-center gap-1">
      <CopyEmailButton email={SUPPORT_EMAIL} />
    </div>
  </>
);

const BlockedView = ({ userData }) => {
  const banUntil = safeToDate(userData?.bannedUntil);
  const isPermanent = !banUntil;

  return (
    <>
      <div className="w-24 h-24 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
        <i
          className="fas fa-ban text-red-500 text-5xl"
          aria-hidden="true"
        />
      </div>
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
        Account Access Denied
      </h1>
      <p className="text-gray-600 dark:text-gray-300 mb-4">
        {userData?.isBanned
          ? 'Your account has been banned.'
          : 'Your account has been blocked.'}{' '}
        Please contact the administrator for more information.
      </p>

      {userData?.blockReason && (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong>Reason:</strong> {userData.blockReason}
          </p>
        </div>
      )}

      {banUntil && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          <i className="fas fa-calendar-times text-red-500 mr-1" aria-hidden="true" />
          Access will be restored on{' '}
          <strong>{formatDateTime(banUntil)}</strong>
        </p>
      )}

      {isPermanent && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">
          This block is <strong>permanent</strong> unless an admin lifts it.
        </p>
      )}

      <a
        href={`mailto:${SUPPORT_EMAIL}?subject=Account%20access%20appeal`}
        className="inline-flex items-center justify-center gap-2 w-full bg-orange hover:bg-orange-dark text-white font-semibold py-2.5 rounded-lg transition"
      >
        <i className="fas fa-envelope" aria-hidden="true" />
        Contact Administrator
      </a>

      <div className="mt-6 flex flex-col items-center gap-1">
        <CopyEmailButton email={SUPPORT_EMAIL} />
      </div>
    </>
  );
};

/* ================================================================== */
/* Main Component                                                      */
/* ================================================================== */

const ApprovalPending = () => {
  const { user, userData: authUserData, logout } = useAuth();
  const navigate = useNavigate();

  const [liveUser, setLiveUser] = useState(authUserData || null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [listenerError, setListenerError] = useState(false);

  const mountedRef = useRef(true);
  const retryRef = useRef(0);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  /* ---------------- Live subscription to user doc ---------------- */
  useEffect(() => {
    if (!user?.uid) return undefined;

    // Seed from context first so we render immediately
    if (authUserData) setLiveUser(authUserData);

    let unsub = null;

    const subscribe = () => {
      unsub = onSnapshot(
        doc(db, 'users', user.uid),
        (snap) => {
          if (!mountedRef.current) return;
          if (snap.exists()) {
            setLiveUser({ id: snap.id, ...snap.data() });
          }
          setListenerError(false);
          retryRef.current = 0;
        },
        (err) => {
          console.warn('Approval status listener:', err);
          if (!mountedRef.current) return;
          setListenerError(true);

          // Exponential backoff: 2s, 4s, 8s, 16s, cap at 30s
          const delay = Math.min(30000, 2000 * 2 ** retryRef.current);
          retryRef.current += 1;

          retryTimerRef.current = setTimeout(() => {
            if (!mountedRef.current) return;
            unsub?.();
            subscribe();
          }, delay);
        },
      );
    };

    subscribe();

    return () => {
      unsub?.();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [user?.uid, authUserData]);

  /* ---------------- Derived status ---------------- */
  const status = useMemo(() => {
    if (!liveUser) return 'unknown';

    const isApproved =
      liveUser.approved === true ||
      liveUser.isApproved === true ||
      liveUser.registrationStatus === 'approved';

    if (liveUser.isBanned === true || liveUser.isBlocked === true) {
      return 'blocked';
    }
    if (liveUser.registrationStatus === 'rejected') {
      return 'rejected';
    }
    if (isApproved) {
      return 'approved';
    }
    return 'pending';
  }, [liveUser]);

  /* ---------------- Auto-redirect once approved ---------------- */
  useEffect(() => {
    if (status !== 'approved') return undefined;
    if (redirecting) return undefined;

    setRedirecting(true);

    const target = liveUser?.isAdmin ? '/admin' : '/';
    const t = setTimeout(() => {
      if (mountedRef.current) navigate(target, { replace: true });
    }, REDIRECT_DELAY_MS);

    return () => clearTimeout(t);
  }, [status, liveUser?.isAdmin, navigate, redirecting]);

  /* ---------------- Time remaining estimate ---------------- */
  const timeRemaining = useMemo(() => {
    const created = safeToDate(liveUser?.createdAt);
    if (!created) return 'Any time now';
    const passed = hoursSince(created);
    const left = SLA_HOURS - passed;
    return formatRemaining(left);
  }, [liveUser?.createdAt]);

  /* ---------------- Sign out ---------------- */
  const handleLogout = useCallback(async () => {
    try {
      if (typeof logout === 'function') {
        await logout();
      } else {
        await signOut(auth);
      }
    } catch (err) {
      console.warn('Sign out failed:', err);
    } finally {
      if (mountedRef.current) {
        navigate('/login', { replace: true });
      }
    }
  }, [logout, navigate]);

  /* ---------------- Manual refresh ---------------- */
  const handleRefresh = useCallback(() => {
    if (!user?.uid) return;
    setRefreshing(true);
    // Trigger a soft re-subscribe by nudging state
    retryRef.current = 0;
    setListenerError(false);
    setTimeout(() => {
      if (mountedRef.current) setRefreshing(false);
    }, 800);
  }, [user?.uid]);

  /* ---------------- Escape closes modal ---------------- */
  useEffect(() => {
    if (!showLogoutConfirm) return undefined;
    const onKey = (e) => e.key === 'Escape' && setShowLogoutConfirm(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showLogoutConfirm]);

  /* ---------------- Body scroll lock for modal ---------------- */
  useEffect(() => {
    if (!showLogoutConfirm) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showLogoutConfirm]);

  /* ================================================================== */
  /* Render                                                              */
  /* ================================================================== */

  // Show skeleton only if we have no data at all (rare — AuthContext should
  // already have seeded us). We avoid showing this when we already have
  // a cached userData value.
  const showSkeleton = !liveUser && !authUserData;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4 py-20">
      {showSkeleton ? (
        <StatusSkeleton />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center overflow-hidden">
            {status === 'approved' && <ConfettiBurst />}

            {status === 'pending' && (
              <PendingView
                user={user}
                userData={liveUser}
                timeRemaining={timeRemaining}
                onSignOut={() => setShowLogoutConfirm(true)}
                onRefresh={handleRefresh}
                refreshing={refreshing}
              />
            )}

            {status === 'rejected' && <RejectedView userData={liveUser} />}

            {status === 'blocked' && <BlockedView userData={liveUser} />}

            {status === 'approved' && (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                  className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <i
                    className="fas fa-check-circle text-green-500 text-5xl"
                    aria-hidden="true"
                  />
                </motion.div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                  You're Approved!
                </h1>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Welcome to the community. Redirecting you now…
                </p>
                <div className="flex justify-center">
                  <div
                    role="status"
                    aria-label="Redirecting"
                    className="w-8 h-8 border-4 border-orange border-t-transparent rounded-full animate-spin"
                  />
                </div>
              </>
            )}

            {status === 'unknown' && (
              <>
                <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i
                    className="fas fa-question text-gray-400 text-5xl"
                    aria-hidden="true"
                  />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                  Status Unknown
                </h1>
                <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm">
                  We couldn't determine your account status. Please refresh or
                  contact support.
                </p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="w-full bg-orange hover:bg-orange-dark text-white font-semibold py-2.5 rounded-lg transition"
                >
                  <i className="fas fa-redo mr-2" aria-hidden="true" />
                  Refresh Page
                </button>
              </>
            )}
          </div>

          {/* Connection warning */}
          <AnimatePresence>
            {listenerError && status !== 'approved' && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="mt-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200 text-xs rounded-lg p-3 flex items-center gap-2"
              >
                <i className="fas fa-wifi" aria-hidden="true" />
                <span>
                  Live updates temporarily unavailable. Auto-retrying…
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Sign out confirmation modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowLogoutConfirm(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="signout-title"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                id="signout-title"
                className="text-lg font-bold mb-2 text-gray-800 dark:text-white"
              >
                Sign Out?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                Are you sure you want to sign out? You can log back in anytime
                to check your approval status.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex-1 bg-orange hover:bg-orange-dark text-white py-2 rounded-lg font-medium transition"
                >
                  Yes, Sign Out
                </button>
                <button
                  type="button"
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg font-medium transition"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ApprovalPending;